import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { DollarSign, FileText, TrendingUp, TrendingDown } from 'lucide-react';
import { cn } from '@/lib/utils';

interface BillingKpiStripProps {
  data?: {
    totalRevenue: number;
    outstandingInvoices: number;
    mrr: number;
    churnRate: number;
  };
  loading: boolean;
  className?: string;
}

export default function BillingKpiStrip({ data, loading, className }: BillingKpiStripProps) {
  const kpis = [
    {
      label: 'Total Revenue',
      value: data?.totalRevenue || 0,
      formatter: (v: number) => `AED ${(v / 1000).toFixed(1)}k`,
      icon: DollarSign,
      color: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-50 dark:bg-green-900/20'
    },
    {
      label: 'Outstanding Invoices',
      value: data?.outstandingInvoices || 0,
      formatter: (v: number) => `AED ${(v / 1000).toFixed(1)}k`,
      icon: FileText,
      color: 'text-orange-600 dark:text-orange-400',
      bgColor: 'bg-orange-50 dark:bg-orange-900/20'
    },
    {
      label: 'Monthly Recurring Revenue',
      value: data?.mrr || 0,
      formatter: (v: number) => `AED ${(v / 1000).toFixed(1)}k`,
      icon: TrendingUp,
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20'
    },
    {
      label: 'Churn Rate',
      value: data?.churnRate || 0,
      formatter: (v: number) => `${v.toFixed(1)}%`,
      icon: TrendingDown,
      color: 'text-red-600 dark:text-red-400',
      bgColor: 'bg-red-50 dark:bg-red-900/20'
    }
  ];

  if (loading) {
    return (
      <div className={cn("grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4", className)}>
        {kpis.map((_, index) => (
          <Card key={index} className="athlyn-card">
            <CardContent className="p-6">
              <Skeleton className="h-4 w-24 mb-2" />
              <Skeleton className="h-8 w-32" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className={cn("grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4", className)}>
      {kpis.map((kpi, index) => {
        const Icon = kpi.icon;
        return (
          <Card key={index} className="athlyn-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">{kpi.label}</p>
                  <p className="text-2xl font-bold tracking-tight">
                    {kpi.formatter(kpi.value)}
                  </p>
                </div>
                <div className={cn("p-3 rounded-lg", kpi.bgColor)}>
                  <Icon className={cn("w-5 h-5", kpi.color)} />
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}